import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleModal } from "../Shared/SimpleModal";
import { SwimlaneDiagram } from "../Shared/SwimlaneDiagram";

const ACTIVE_STATUSES = new Set(["1", "2", "6"]);

const STAGE_NAME = { "1": "Open", "2": "Active", "6": "Reopen" };

const STAGE_COLOR = {
  Open: "text-blue-600",
  Active: "text-amber-600",
  Reopen: "text-rose-500",
};

const PRI_DOT = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-400",
  low: "bg-green-500",
};

const PRI_PILL = {
  critical: "bg-red-50 text-red-600 border-red-200",
  high: "bg-orange-50 text-orange-600 border-orange-200",
  medium: "bg-yellow-50 text-yellow-600 border-yellow-200",
  low: "bg-green-50 text-green-600 border-green-200",
};

function shortName(email) {
  if (!email) return "Unassigned";
  return email
    .split("@")[0]
    .split(".")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getOverdueDays(deadline) {
  if (!deadline) return 0;
  const dl = new Date(deadline);
  if (isNaN(dl.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dl.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - dl) / 86400000));
}

function calculateAge(deadline) {
  const days = getOverdueDays(deadline);
  if (days <= 0) return null;
  if (days >= 365) {
    const y = Math.floor(days / 365);
    return `${y} Year${y > 1 ? "s" : ""} ago`;
  }
  if (days >= 30) {
    const m = Math.floor(days / 30);
    return `${m} Month${m > 1 ? "s" : ""}`;
  }
  if (days >= 1) {
    return `${days} Day${days > 1 ? "s" : ""}`;
  }
  return "< 1 Day";
}

const TICKER_CSS = `
@keyframes ticker-fadein {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ticker-fadein { animation: ticker-fadein 0.5s ease-out; }
.ticker-scroll::-webkit-scrollbar { height: 3px; }
.ticker-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
.ticker-scroll::-webkit-scrollbar-track { background: transparent; }
`;

export const LatestTickets = ({
  tickets = [],
  username = "",
  access = 1,
  priorities = [],
  statuses = [],
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [swimlaneOpen, setSwimlaneOpen] = useState(false);
  const [swimlaneTicket, setSwimlaneTicket] = useState(null);
  const authToken = localStorage.getItem("authToken");

  function openSwimlane(t) {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${t.ticketId}/history`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      }
    )
      .then((res) => (res.status === 401 ? null : res.json()))
      .then((data) => {
        if (!data) return;
        setSwimlaneTicket({
          id: t.ticketId,
          currentStage: t.stageName,
          team: t.org || "Default",
          assign: t.assign,
          created: t.createdBy || "System",
          createdDate: t.createdAt,
          history: Array.isArray(data) ? data : [],
        });
        setSwimlaneOpen(true);
      })
      .catch((err) => console.error("Swimlane fetch error:", err));
  }

  const getPriorityName = useCallback(
    (val) => {
      const p = priorities.find((x) => String(x.value) === String(val));
      return p ? p.key : `P${val}`;
    },
    [priorities]
  );

  const priKey = useCallback(
    (val) => getPriorityName(val).toLowerCase(),
    [getPriorityName]
  );

  const overdueTickets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return tickets
      .filter((t) => {
        if (!ACTIVE_STATUSES.has(String(t.status))) return false;
        if (access < 3 && t.assign !== username) return false;
        if (!t.deadline) return false;
        const dl = new Date(t.deadline);
        if (isNaN(dl.getTime())) return false;
        dl.setHours(0, 0, 0, 0);
        return dl < today;
      })
      .map((t) => ({
        ...t,
        stageName: STAGE_NAME[String(t.status)] || "Unknown",
        priorityName: getPriorityName(t.priority),
        overdueDays: getOverdueDays(t.deadline),
      }))
      .sort((a, b) => b.overdueDays - a.overdueDays);
  }, [tickets, username, access, getPriorityName]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || overdueTickets.length === 0) return;

    const step = () => {
      if (!pausedRef.current && el) {
        el.scrollLeft += 0.5;
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [overdueTickets]);

  useEffect(() => {
    if (!expanded) return;
    const onEsc = (e) => {
      if (e.key === "Escape") setExpanded(false);
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [expanded]);

  const handleWheel = useCallback((e) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY * 0.3;
    }
  }, []);

  if (overdueTickets.length === 0) return null;

  const isUnassigned = (assign) => !assign || assign.trim() === "";

  const renderTickerItem = (t, prefix) => {
    const pk = priKey(t.priority);
    const dotCls = PRI_DOT[pk] || "bg-gray-400";
    const stageCls = STAGE_COLOR[t.stageName] || "text-gray-500";
    const unassigned = isUnassigned(t.assign);
    const age = calculateAge(t.deadline);

    return (
      <div
        key={`${prefix}-${t.ticketId}`}
        className="inline-flex items-center gap-2.5 mx-8 cursor-pointer hover:opacity-70 transition-opacity shrink-0"
        onClick={() => navigate(`/Ticket/${t.ticketId}`)}
      >
        <span
          className={`w-2 h-2 rounded-full ring-2 ring-gray-200 shrink-0 ${dotCls}`}
        />
        <span className="text-xs font-bold text-gray-800 whitespace-nowrap">
          {t.ticketId}
        </span>
        <span className="text-[11px] text-gray-500 whitespace-nowrap">
          {t.org || "—"}
        </span>
        <span className="text-gray-300">•</span>
        <span
          className={`text-[11px] whitespace-nowrap ${
            unassigned ? "font-bold text-amber-600" : "text-gray-500"
          }`}
        >
          {unassigned ? "Unassigned" : shortName(t.assign)}
        </span>
        <span className={`text-[10px] font-semibold whitespace-nowrap ${stageCls}`}>
          {t.stageName}
        </span>
        {age && (
          <span className="text-[10px] font-bold bg-red-50 border border-red-200 text-red-600 rounded-full px-1.5 py-0.5 whitespace-nowrap">
            Overdue: {age}
          </span>
        )}
        <span className="text-gray-200">|</span>
      </div>
    );
  };

  return (
    <>
      <style>{TICKER_CSS}</style>

      {/* ── Ticker Bar ── */}
      <div className="ticker-fadein rounded-xl shadow-sm border border-gray-200 overflow-hidden flex items-stretch bg-white">
        {/* Left Badge */}
        <div className="shrink-0 bg-white border-r border-gray-200 px-4 py-1 flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-400" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-rose-500 whitespace-nowrap">
            Attention Required
          </span>
          <span className="text-[10px] font-bold rounded-full bg-rose-50 text-rose-500 border border-rose-200 px-1.5 py-0.5 leading-none">
            {overdueTickets.length}
          </span>
        </div>

        {/* Center Ticker */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto ticker-scroll flex items-center"
          onMouseEnter={() => {
            pausedRef.current = true;
          }}
          onMouseLeave={() => {
            pausedRef.current = false;
          }}
          onWheel={handleWheel}
        >
          <div className="flex w-max items-center py-1">
            {overdueTickets.map((t) => renderTickerItem(t, "a"))}
            {overdueTickets.map((t) => renderTickerItem(t, "b"))}
          </div>
        </div>

        {/* Right Expand Button */}
        <button
          className="shrink-0 bg-white border-l border-gray-200 px-3 hover:bg-rose-50 transition-colors flex items-center justify-center"
          onClick={() => setExpanded(true)}
          title="Expand"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-gray-400"
          >
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      </div>

      {/* ── Expanded Modal ── */}
      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setExpanded(false);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ width: "95%", maxWidth: "80rem", maxHeight: "90vh" }}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 shrink-0">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-400" />
              </span>
              <span className="text-sm font-black text-gray-800">
                Attention Required
              </span>
              <span className="text-[11px] font-bold rounded-full bg-red-50 text-red-600 border border-red-200 px-2 py-0.5">
                {overdueTickets.length}
              </span>
              <button
                className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors text-lg font-bold"
                onClick={() => setExpanded(false)}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1">
              <table
                className="w-full border-collapse"
                style={{ fontSize: "11px" }}
              >
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr>
                    {[
                      "Ticket ID",
                      "Application",
                      "Assignee",
                      "Priority",
                      "Status",
                      "Deadline",
                      "Overdue",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider text-gray-500 border-b border-gray-200"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overdueTickets.map((t, idx) => {
                    const pk = priKey(t.priority);
                    const pillCls = PRI_PILL[pk] || "bg-gray-50 text-gray-600 border-gray-200";
                    const stageCls = STAGE_COLOR[t.stageName] || "text-gray-500";
                    const unassigned = isUnassigned(t.assign);
                    const age = calculateAge(t.deadline);

                    return (
                      <tr
                        key={t.ticketId}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        }`}
                        onClick={() => {
                          setExpanded(false);
                          navigate(`/Ticket/${t.ticketId}`);
                        }}
                      >
                        <td className="px-4 py-2.5 border-b border-gray-100 whitespace-nowrap">
                          <span className="text-jiojbm60 font-bold">
                            {t.ticketId}
                          </span>
                          <button
                            className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-100 text-gray-400 hover:text-jiojbm60 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSwimlane(t);
                            }}
                            title="View Swimlane"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-4 py-2.5 border-b border-gray-100 text-gray-600">
                          {t.org || "—"}
                        </td>
                        <td className="px-4 py-2.5 border-b border-gray-100 whitespace-nowrap">
                          {unassigned ? (
                            <span className="font-bold text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">
                              Unassigned
                            </span>
                          ) : (
                            <span className="text-gray-600">
                              {shortName(t.assign)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 border-b border-gray-100">
                          <span
                            className={`inline-block text-[10px] font-bold rounded-full px-2 py-0.5 border ${pillCls}`}
                          >
                            {t.priorityName}
                          </span>
                        </td>
                        <td
                          className={`px-4 py-2.5 border-b border-gray-100 font-semibold ${stageCls}`}
                        >
                          {t.stageName}
                        </td>
                        <td className="px-4 py-2.5 border-b border-gray-100 text-gray-500 whitespace-nowrap">
                          {t.deadline
                            ? new Date(t.deadline).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="px-4 py-2.5 border-b border-gray-100">
                          {age && (
                            <span className="text-[10px] font-bold bg-red-50 border border-red-200 text-red-600 rounded-full px-2 py-0.5 whitespace-nowrap">
                              {age}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Swimlane Modal ── */}
      <SimpleModal
        open={swimlaneOpen}
        title={swimlaneTicket ? `Swimlane \u2014 ${swimlaneTicket.id}` : "Swimlane"}
        onClose={() => setSwimlaneOpen(false)}
        maxWidth="max-w-6xl"
        heightClass="max-h-[85vh]"
      >
        {swimlaneTicket && <SwimlaneDiagram ticket={swimlaneTicket} />}
      </SimpleModal>
    </>
  );
};
